import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
} from "@mui/material";
import { motion } from "framer-motion";
import SwanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";
import SwanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";

const haptic = {
    whileTap: { scale: 0.97 },
    whileHover: { scale: 1.03 },
    transition: { type: "spring", stiffness: 300, damping: 17 },
};

const capabilities = [
    {
        title: "Federated Data Layer",
        description:
            "Connect all your databases — SQL Server, PostgreSQL, MySQL, or anything else — into one unified query interface. AIOS routes requests, merges results, and eliminates data silos instantly.",
    },
    {
        title: "Autonomous AI Agents",
        description:
            "Deploy AI agents that handle tasks across your systems independently. Reporting, data syncing, anomaly detection, compliance checks — running 24/7 without human intervention.",
    },
    {
        title: "Schema Intelligence",
        description:
            "AIOS automatically discovers every table, field, and relationship across your databases. It snapshots schemas, detects changes, and maps data lineage — so you always know your data.",
    },
    {
        title: "Knowledge Graph",
        description:
            "A living, growing map of every entity, relationship, and business rule across your organization. Context that gets smarter with every query and every interaction.",
    },
    {
        title: "Decision Tracking & Audit",
        description:
            "Every agent action is logged with full reasoning. Auditable, explainable, and reviewable — critical for regulated industries and compliance-sensitive environments.",
    },
    {
        title: "Continuous Learning",
        description:
            "Feedback from every query, decision, and outcome feeds back into the system automatically. Your AI infrastructure gets smarter over time without manual retraining.",
    },
];

const useCases = [
    {
        title: "Enterprise AI Infrastructure",
        description:
            "Build AI-powered applications on top of AIOS instead of starting from scratch. Federated data, agent coordination, and a knowledge graph — ready to go.",
    },
    {
        title: "Multi-Database Intelligence",
        description:
            "Organizations with data spread across dozens of databases use AIOS to query, analyze, and act on all of it from a single platform.",
    },
    {
        title: "Automated Operations",
        description:
            "Deploy AI agents that run reports, sync data, flag anomalies, and execute workflows across your systems — no human in the loop required.",
    },
    {
        title: "Compliance & Audit",
        description:
            "Every decision logged, every action traceable. AIOS provides the transparency and auditability that regulated industries demand.",
    },
];

export default function Aios() {
    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background:
                        "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    minHeight: { xs: "50vh", md: "60vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={SwanAiLabsLogo}
                    alt="AIOS"
                    sx={{
                        height: { xs: 80, md: 130 },
                        mb: 2,
                        filter: "drop-shadow(0 4px 32px rgba(0,207,254,0.25))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.4rem", md: "3.6rem" },
                        letterSpacing: "-0.04em",
                        mb: 1,
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    AIOS
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                        mb: 2,
                        color: "rgba(255,255,255,0.95)",
                    }}
                >
                    Agentic Operating System
                </Typography>
                <Typography
                    sx={{
                        maxWidth: 780,
                        mx: "auto",
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 400,
                        fontSize: { xs: "1.05rem", md: "1.2rem" },
                        textShadow: "0 2px 14px rgba(30,140,160,0.13)",
                    }}
                >
                    AI infrastructure for any company. Federate your databases, deploy autonomous agents,
                    and build intelligent systems — without starting from scratch.
                </Typography>
            </Box>

            {/* What is AIOS */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, mb: 4 }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                            textAlign: "center",
                        }}
                    >
                        Build AI-Powered Systems on a Proven Foundation
                    </Typography>
                    <Typography
                        sx={{
                            color: "text.secondary",
                            fontSize: "1.15rem",
                            textAlign: "center",
                            maxWidth: 820,
                            mx: "auto",
                            mb: 2,
                        }}
                    >
                        AIOS is the same platform that powers every Swan Solutions product — FIG Finance,
                        Hi5, our CRM, and dozens more. Now it's available for your organization.
                        Connect your databases, deploy AI agents, and start building intelligent
                        applications on day one.
                    </Typography>
                </motion.div>
            </Container>

            {/* Capabilities */}
            <Box sx={{ bgcolor: "#eaf5fa", py: { xs: 6, md: 10 } }}>
                <Container maxWidth="xl">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: "primary.main",
                                mb: 5,
                                textAlign: "center",
                            }}
                        >
                            What You Get
                        </Typography>
                    </motion.div>
                    <Grid container spacing={5} justifyContent="center">
                        {capabilities.map((cap, i) => (
                            <Grid item xs={12} md={6} lg={4} key={cap.title}>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    {...haptic}
                                    transition={{ duration: 0.5 + i * 0.09 }}
                                    style={{ height: "100%" }}
                                >
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: "100%",
                                            borderRadius: 4,
                                            boxShadow: "0 3px 28px 0 rgba(20,76,100,0.07)",
                                            p: 3,
                                        }}
                                    >
                                        <CardContent>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1.5,
                                                    color: "primary.main",
                                                    fontSize: "1.15rem",
                                                }}
                                            >
                                                {cap.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "1.05rem",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {cap.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Use Cases */}
            <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 5,
                            textAlign: "center",
                        }}
                    >
                        Who It's For
                    </Typography>
                </motion.div>
                <Grid container spacing={5} justifyContent="center">
                    {useCases.map((uc, i) => (
                        <Grid item xs={12} md={6} key={uc.title}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                {...haptic}
                                transition={{ duration: 0.5 + i * 0.09 }}
                                style={{ height: "100%" }}
                            >
                                <Card
                                    elevation={3}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        borderRadius: 4,
                                        px: 2,
                                        py: 3,
                                        boxShadow: "0 3px 28px 0 rgba(20,76,100,0.07)",
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 2,
                                                color: "primary.main",
                                                fontSize: "1.25rem",
                                            }}
                                        >
                                            {uc.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: "text.primary",
                                                fontSize: "1.09rem",
                                            }}
                                        >
                                            {uc.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* CTA */}
            <Box sx={{ textAlign: "center", py: { xs: 6, md: 10 }, bgcolor: "#eaf8fb" }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Box
                        component="img"
                        src={SwanSolutionsLogo}
                        alt="Swan Solutions"
                        sx={{
                            height: { xs: 52, md: 72 },
                            mb: 2,
                            filter: "drop-shadow(0 4px 16px rgba(20, 120, 120, 0.12))",
                        }}
                    />
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                        }}
                    >
                        Ready to Build on AIOS?
                    </Typography>
                    <Typography
                        sx={{
                            maxWidth: 650,
                            mx: "auto",
                            color: "text.secondary",
                            fontSize: "1.13rem",
                            mb: 3,
                        }}
                    >
                        Whether you're looking to federate your data, deploy AI agents, or build
                        intelligent applications — let's talk about how AIOS can work for your organization.
                    </Typography>
                    <Button
                        href="/contact"
                        variant="contained"
                        size="large"
                        component={motion.a}
                        {...haptic}
                        sx={{
                            borderRadius: 99,
                            fontWeight: 700,
                            px: 5,
                            py: 1.4,
                            background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                            fontSize: "1.12rem",
                        }}
                    >
                        Get in Touch
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
}
